import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, Image as ImageIcon } from "lucide-react";
import Iterator from "../utils/Iterator";
import { IMG_URl } from "src/common/img_url";
import { indexStore } from "src/store/indexStore";
import { Skeleton } from "../ui/skeleton";
import Visibility from "../utils/Visibility";
import { Card } from "../ui/card";

const Row = ({ name, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.element.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      className={`flex gap-4 items-center p-4 border-2 ${
        isDragging
          ? "shadow-lg border-primary bg-primary/5 opacity-90"
          : "hover:shadow-md hover:border-primary/20"
      }`}
      key={props.element.id}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <div
        ref={setActivatorNodeRef}
        className="p-2 rounded-md hover:bg-muted cursor-grab active:cursor-grabbing transition-colors"
        style={{
          touchAction: "none",
        }}
        {...listeners}
      >
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
        {props.element.image ? (
          <img
            className="w-full h-full object-cover"
            src={`${IMG_URl}/${props.element.image}`}
            alt={props.element[name] || ""}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-muted-foreground/30" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-semibold text-foreground line-clamp-1">
          {props.element[name] || ""}
        </p>
      </div>
      {isDragging && (
        <div className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
          {props.element.sort_num || ""}
        </div>
      )}
    </Card>
  );
};

const App = (props) => {
  const [data, setData] = useState([]);
  const { nameLang } = indexStore();
  const { t } = useTranslation();
  useEffect(() => {
    setData(props.dataSource);
  }, [props.dataSource]);

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const newData = [];
      setData((previous) => {
        const activeIndex = previous.findIndex((i) => i.id === active.id);
        const overIndex = previous.findIndex((i) => i.id === over?.id);

        // Use the spread operator to create a new array with the updated order
        const updatedData = arrayMove(previous, activeIndex, overIndex);

        // Add the updated data to newData
        newData.push(...updatedData);

        // Return the updated array to set the state
        return updatedData;
      });
      let submitData = [];
      newData.forEach((item, index) => {
        submitData.push({
          id: item.id,
          sortNum: index + 1,
        });
      });

      props.onMove(submitData);
    }
  };

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
        items={data.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-3">
          <Visibility visible={!props.loading}>
            {data.length > 0 ? (
              <Iterator
                data={data}
                render={(e, i) => <Row element={e} key={e.id} name={nameLang} />}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <ImageIcon className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-semibold mb-2">
                  {t("gloabl.no_items_to_sort") || "No items to sort"}
                </p>
                <p className="text-sm">
                  {t("gloabl.no_items_in_category_sort") || "This category doesn't have any items yet"}
                </p>
              </div>
            )}
          </Visibility>
          <Visibility visible={props.loading}>
            <Iterator
              data={[1, 2, 3, 4]}
              render={(e, i) => <RowLoading key={i} />}
            />
          </Visibility>
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default App;

const RowLoading = () => (
  <Card className="flex gap-4 items-center p-4 border-2">
    <Skeleton className="w-9 h-9 rounded-md" />
    <Skeleton className="w-20 h-20 rounded-lg flex-shrink-0" />
    <Skeleton className="h-5 flex-1" />
  </Card>
);
